import { useEffect, useState } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import HomeIcon from '@mui/icons-material/Home';
import MailIcon from '@mui/icons-material/Mail';
import EventNoteIcon from '@mui/icons-material/EventNote';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { useTranslation } from 'react-i18next';
import { buildServiceHref, getHomeHref } from '../utils/routing';

type SectionId = 'invitation' | 'programme' | 'home' | 'gallery' | 'upload';

interface NavItem {
  id: SectionId;
  labelKey: string;
  href: string;
  Icon: typeof HomeIcon;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'invitation', labelKey: 'home.invitation', href: buildServiceHref('invitation'), Icon: MailIcon },
  { id: 'programme', labelKey: 'home.programme', href: buildServiceHref('programme'), Icon: EventNoteIcon },
  { id: 'home', labelKey: 'common.home', href: getHomeHref(), Icon: HomeIcon },
  { id: 'gallery', labelKey: 'home.gallery', href: buildServiceHref('gallery'), Icon: PhotoLibraryIcon },
  { id: 'upload', labelKey: 'home.upload', href: buildServiceHref('upload'), Icon: PhotoCameraIcon },
];

const DESKTOP_NAV_ITEMS = [
  NAV_ITEMS.find((item) => item.id === 'home')!,
  ...NAV_ITEMS.filter((item) => item.id !== 'home'),
];
const NAV_LABELS_KEY = 'wedding_nav_labels';
const MENU_BUTTON_OFFSET = 64;
const DRAWER_WIDTH = 280;

function currentSection(): SectionId {
  const path = window.location.pathname;
  if (path.startsWith('/invitation')) return 'invitation';
  if (path.startsWith('/programme')) return 'programme';
  if (path.startsWith('/gallery')) return 'gallery';
  if (path.startsWith('/upload')) return 'upload';
  return 'home';
}

function isEmbeddedFrame(): boolean {
  try {
    return window.top !== window;
  } catch {
    return true;
  }
}

function labelPack(tr: (key: string) => string) {
  return {
    invitation: tr('home.invitation'),
    programme: tr('home.programme'),
    home: tr('common.home'),
    gallery: tr('home.gallery'),
    upload: tr('home.upload'),
  };
}

const SHELL_LABEL_KEYS = ['invitation', 'programme', 'home', 'gallery', 'upload'] as const;

function applyShellLabels(pack: ReturnType<typeof labelPack>) {
  const shell = document.getElementById('guest-nav-shell');
  if (!shell) return;
  const links = shell.querySelectorAll('a');
  links.forEach((link, index) => {
    const key = SHELL_LABEL_KEYS[index];
    if (!key) return;
    const spans = link.querySelectorAll('span');
    const label = spans[spans.length - 1];
    if (label) label.textContent = pack[key];
  });
}

export default function GuestNav() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(false);
  const active = currentSection();
  const embedded = isEmbeddedFrame();
  const hasShell = typeof document !== 'undefined' && document.getElementById('guest-nav-shell') !== null;

  useEffect(() => {
    if (phone) setOpen(false);
  }, [phone]);

  useEffect(() => {
    if (embedded || hasShell) return;
    document.documentElement.classList.add('has-guest-nav');
    return () => {
      document.documentElement.classList.remove('has-guest-nav');
    };
  }, [embedded, hasShell]);

  useEffect(() => {
    const write = () => {
      const en = labelPack(i18n.getFixedT('en_hk'));
      const zh = labelPack(i18n.getFixedT('zh_hk'));
      try {
        localStorage.setItem(NAV_LABELS_KEY, JSON.stringify({ en, zh }));
      } catch { /* private mode or a full store */ }
      applyShellLabels(i18n.language === 'zh_hk' ? zh : en);
    };
    write();
    i18n.on('languageChanged', write);
    i18n.on('added', write);
    return () => {
      i18n.off('languageChanged', write);
      i18n.off('added', write);
    };
  }, [i18n]);

  if (embedded) return null;

  return (
    <>
      <IconButton
        onClick={() => setOpen((value) => !value)}
        aria-label={t('common.menu')}
        aria-expanded={open}
        size="small"
        sx={{
          display: { xs: 'none', sm: 'inline-flex' },
          position: 'fixed',
          top: 12,
          left: 12,
          zIndex: (muiTheme) => muiTheme.zIndex.modal + 1,
          bgcolor: 'rgba(255,255,255,0.55)',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 1,
          width: 40,
          height: 40,
          '&:hover': { bgcolor: 'rgba(255,255,255,0.8)' },
        }}
      >
        <Box aria-hidden sx={{ position: 'relative', width: 18, height: 14 }}>
          {[0, 1, 2].map((index) => (
            <Box
              key={index}
              sx={{
                position: 'absolute',
                left: 0,
                width: 18,
                height: 2,
                borderRadius: 1,
                bgcolor: 'currentColor',
                transformOrigin: 'center',
                transition: 'transform 200ms ease, opacity 200ms ease',
                top: index * 6,
                ...(open && index === 0 && { transform: 'translateY(6px) rotate(45deg)' }),
                ...(open && index === 1 && { opacity: 0 }),
                ...(open && index === 2 && { transform: 'translateY(-6px) rotate(-45deg)' }),
              }}
            />
          ))}
        </Box>
      </IconButton>

      <Drawer
        anchor="left"
        open={open}
        onClose={() => setOpen(false)}
        elevation={0}
        slotProps={{
          paper: {
            sx: {
              width: DRAWER_WIDTH + MENU_BUTTON_OFFSET,
              bgcolor: 'background.paper',
              backgroundImage: 'none',
              boxShadow: 16,
            },
          },
        }}
      >
        <Box
          component="nav"
          aria-label={t('common.menu')}
          sx={{
            pl: `${MENU_BUTTON_OFFSET}px`,
            width: '100%',
            height: '100%',
            pt: 1,
            boxSizing: 'border-box',
          }}
        >
          <List>
            {DESKTOP_NAV_ITEMS.map((item) => {
              const selected = item.id === active;
              return (
                <ListItemButton
                  key={item.id}
                  component="a"
                  href={item.href}
                  selected={selected}
                  onClick={() => setOpen(false)}
                >
                  <ListItemIcon sx={{ color: selected ? 'primary.main' : 'inherit', minWidth: 40 }}>
                    <item.Icon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t(item.labelKey)}
                    sx={{ '& .MuiListItemText-primary': { fontWeight: selected ? 700 : 500 } }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Box>
      </Drawer>

      {!hasShell && (
      <Box
        component="nav"
        aria-label={t('common.menu')}
        sx={{
          display: { xs: 'flex', sm: 'none' },
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1200,
          alignItems: 'flex-end',
          justifyContent: 'space-around',
          bgcolor: 'rgba(255,255,255,0.96)',
          borderTop: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 -2px 12px rgba(0,0,0,0.06)',
          px: 0.5,
          pt: 0.75,
          pb: 'calc(16px + env(safe-area-inset-bottom, 0px))',
          viewTransitionName: 'guest-nav',
        }}
      >
        {NAV_ITEMS.map((item) => {
          const selected = item.id === active;
          if (item.id === 'home') {
            return (
              <Box
                key={item.id}
                component="a"
                href={item.href}
                aria-current={selected ? 'page' : undefined}
                sx={{
                  flex: '1 1 0',
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  mt: -2.5,
                  textDecoration: 'none',
                  color: selected ? 'primary.dark' : 'text.secondary',
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: selected ? 'primary.dark' : 'primary.main',
                    color: 'primary.contrastText',
                    border: '3px solid',
                    borderColor: 'background.paper',
                    boxShadow: 3,
                  }}
                >
                  <HomeIcon />
                </Box>
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ mt: 0.25, maxWidth: '100%', fontWeight: 700, lineHeight: 1.1, fontSize: '0.65rem' }}
                >
                  {t(item.labelKey)}
                </Typography>
              </Box>
            );
          }

          return (
            <Box
              key={item.id}
              component="a"
              href={item.href}
              aria-current={selected ? 'page' : undefined}
              sx={{
                flex: '1 1 0',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.25,
                py: 0.75,
                textDecoration: 'none',
                color: selected ? 'primary.main' : 'text.secondary',
              }}
            >
              <item.Icon sx={{ fontSize: 22 }} />
              <Typography
                variant="caption"
                noWrap
                sx={{ maxWidth: '100%', fontWeight: selected ? 700 : 500, lineHeight: 1.1, fontSize: '0.65rem' }}
              >
                {t(item.labelKey)}
              </Typography>
            </Box>
          );
        })}
      </Box>
      )}
    </>
  );
}
